import { lens, withLenses } from '@dhmk/zustand-lens';
import type { GridInitialState } from '@mui/x-data-grid-premium';
import type { ServerGridFilter } from 'src/modules/grids/shared/gridFilterTypes';
import { createUseStoreState } from 'src/utils/createUseStoreState';
import { persist } from 'zustand/middleware';
import { immer } from 'zustand/middleware/immer';

/** The saved grid state: what `dataGridStateToConfig` keeps, plus the two server-side extras the toolbar adds. */
export type ViewConfigPayload = GridInitialState & {
  quickFilter?: string;
  serverFilters?: ServerGridFilter[];
  /** LEGACY nesting, written only by the pre-rebuild inventory cell; read as a fallback, never written. */
  grid?: { columns?: { orderedFields?: string[] } };
  [key: string]: unknown;
};

/** One saved view of `viewConfigurationPage(page:)`. `id` is an `Int!` on the schema, so it is a number. */
export type ViewConfigRow = { id: number; name: string; config: ViewConfigPayload };

export type ViewConfigState = {
  config: ViewConfigPayload | null;
  dirty: boolean;
  /** `_setConfig` writes the selected row onto the lens slice; selectors read it back here. */
  view?: ViewConfigRow | null;
};

export type ViewConfigActions = { _setConfig: (config: ViewConfigRow | null) => void };

export type ViewConfigStore = ViewConfigState & ViewConfigActions;

export { VIEW_PAGES } from './viewConfigPages';

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null && !Array.isArray(value);

/**
 * Called, not spread as a constant: a generic `merge` on a shared object hands `persist` a second
 * inference candidate for its store type and collapses it to `unknown`. Instantiating at the call
 * site lets the state creator alone decide `S`.
 */
export const persistViewConfig = <S>() => ({
  version: 1,
  migrate: (persistedState: unknown, version: number): Record<string, unknown> | undefined => {
    if (!isRecord(persistedState)) {
      return;
    }

    // move state into its namespace
    if (version === 0) {
      persistedState.view = { ...persistedState };
    }

    return persistedState;
  },
  merge: (persistedState: unknown, currentState: S): S => {
    if (!isRecord(persistedState)) {
      return currentState;
    }

    const currentView = isRecord(currentState) ? currentState.view : undefined;
    const persistedView = isRecord(persistedState.view) ? persistedState.view : undefined;

    return Object.assign({}, currentState, persistedState, {
      view: { ...(isRecord(currentView) ? currentView : undefined), ...persistedView },
    });
  },
});

export const createViewConfigStore = (storageKey: string) =>
  persist(immer(withLenses({ view: lens((set) => createViewConfigSlice(set)) })), {
    name: `view-config-${storageKey}`,
    ...persistViewConfig(),
  });

type ViewConfigSet = (recipe: (state: ViewConfigState) => void) => void;

export const createViewConfigSlice = (set: ViewConfigSet): ViewConfigStore => ({
  config: null,
  dirty: false,
  _setConfig: (config) =>
    set((state) => {
      state.view = config;
    }),
});

export const useViewConfigStore = createUseStoreState<ViewConfigStore>('view');

/**
 * Only saves the necessary parts of the data grid state to the database.
 */
export const dataGridStateToConfig = (state: GridInitialState): ViewConfigPayload => {
  const config: ViewConfigPayload = {
    aggregation: state.aggregation,
    columns: {
      columnVisibilityModel: state.columns?.columnVisibilityModel,
      orderedFields: state.columns?.orderedFields,
    },
    filter: state.filter,
    pinnedColumns: state.pinnedColumns,
    rowGrouping: state.rowGrouping,
    sorting: state.sorting,
  };
  Object.keys(config).forEach((key) => {
    if (!config[key]) delete config[key];
  });
  return config;
};

/** `pinnedColumns` is `GridPinnedColumnFields` — an OBJECT (`{ left?, right? }`), never an array; `[]` typechecks only because `ViewConfigPayload` carries an `[key: string]: unknown` index signature, and this value feeds straight to `apiRef.restoreState`. */
export const emptyGridState: ViewConfigPayload = {
  aggregation: undefined,
  columns: { columnVisibilityModel: {}, orderedFields: [] },
  filter: undefined,
  pinnedColumns: {},
  rowGrouping: undefined,
  sorting: undefined,
  serverFilters: [],
};

/**
 * Helper function to get the column order from the view config.
 * (This is helpful for only basic data grids - Data grid with
 * custom config settings will likely need to use their own selector)
 *
 *   dataGridStateToConfig ──▶ { columns: { orderedFields } , … }   ◄── canonical
 *   pre-rebuild inventory ──▶ { globGroup, grid: { columns: … } }  ◄── legacy
 *
 * Every rebuilt page took a fresh `-v2` VIEW_PAGES key, so only the canonical
 * shape exists for those; `VIEW_PAGES.INVENTORY` kept the key `'inventory'`, so
 * rows written by the old inventory cell are still served under it.
 * @returns {string[]} The column order.
 */
export const selectViewColumnOrder = (view: ViewConfigRow | null | undefined): string[] | undefined =>
  view?.config?.columns?.orderedFields ?? view?.config?.grid?.columns?.orderedFields;

export const useViewColumnOrder = () => {
  return useViewConfigStore((store) => selectViewColumnOrder(store.view));
};
